/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IGiftCardExtend } from '@msdyn365-commerce/global-state';
import {
    Address, AddressPurpose, CountryRegionInfo, isNullOrWhitespace, StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { action, computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AutoSuggest } from '../../common/address/address-autosuggest';
import { IAutoSuggestOptions } from '../../common/address/address-autosuggest.data';
import { AddressCommon } from '../../common/address/address-common';
import { AddressFormat } from '../../common/address/address-format';
import { AddressItemType, IAddressDropdownsData } from '../../common/address/address-format.data';
import { AddressMetaData } from '../../common/address/address-meta-data';
import {
    AddressOperation, AddressType, IAddressResource, IAddressResponse
} from '../../common/address/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from '../../common/address/components/address-add';
import AddressBillingCheckoxComponent from '../../common/address/components/address-billing-checkbox';
import AddressBillingHeadingComponent from '../../common/address/components/address-billing-heading';
import { AddressShow, IAddressShowProps } from '../../common/address/components/address-show';
import AddressSearch, { IFormattedAddressDetails } from '../../components/citta-address-search/citta-address-search';
import { ICittaCheckoutBillingAddressData } from './citta-checkout-billing-address.data';
import { ICittaCheckoutBillingAddressProps } from './citta-checkout-billing-address.props.autogenerated';

export interface IProps extends ICittaCheckoutBillingAddressProps<ICittaCheckoutBillingAddressData>, IModuleStateProps { }

export interface ICheckoutBillingAddressViewState {
    isShowAddress: boolean;
    isShowAddOrUpdateAddress: boolean;
    isShowSameAsShippingCheckbox: boolean;
}

export interface ICheckoutBillingAddressViewProps extends IProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutBillingAddressViewState;
    CheckoutBillingAddress: IModuleProps;
    sameAsShippingCheckbox: React.ReactNode;
    heading: React.ReactNode;
    showAddress: IAddressShowProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * CheckoutBillingAddress component
 * @extends {React.PureComponent<ICittaCheckoutBillingAddressProps<ICittaCheckoutBillingAddressData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutBillingAddress extends React.Component<IProps> {

    @observable private currentOperation: AddressOperation;
    @observable private isBillingAddressSameAsShipping: boolean;
    @observable private selectedAddress?: Address;
    @observable private addUpdateAddress: Address;
    @observable private countryRegionId: string = 'NZL';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private manualAddress: boolean;

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private addressPurposes: AddressPurpose[];
    private resources: IAddressResource;
    private defaultAddressType: number = this.props.context.app.config.defaultAddressTypeValue || 7; // Default to Home
    // private addRenderRef: React.RefObject<AddressAdd>;
    private telemetryContent?: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    private isClickAndCollectOrder: boolean;
    private customerAddresses: Address[];
    private isNZ: boolean = false;
    private addressDropdownsData:IAddressDropdownsData = {};
    private showAddressSearch: boolean = false;
    private nzlCountryRegionId: string = 'NZL';
    private showBorderClass: string;

    constructor(props: IProps) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.addUpdateAddress = {};
        this.validationError = {};
        this.isBillingAddressSameAsShipping = false;
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.addressPurposes = data.addressPurposes.result || [];
        this.countryRegions = data.countryRegions.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(this.countryRegions, new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()), this.addressPurposes);
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this.isNZ = this.selectedAddress && this.selectedAddress.ThreeLetterISORegionName === this.nzlCountryRegionId || false;
        // this.manualAddress = false;
        this.manualAddress = true;
        this.showBorderClass = 'show-border';
        // this.addRenderRef = React.createRef();

        this.isClickAndCollectOrder = false;
        this.customerAddresses = data.address.result || [];
    }

    // tslint:disable-next-line:max-func-body-length
    public async componentDidMount(): Promise<void> {

        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: {
                        channel
                    }
                }
            },
            config: {
                autoSuggestionEnabled,
                autoSuggestOptions
            }
        } = this.props;

        this.props.data.checkout.then(() => {
            this._setDefaultCountryRegionId();
            this._handleCartHasOnlyGiftCardScenario();
            this._initModuleState();
        });

        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        reaction(
            () => this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress,
            shippingAddress => {
                if (this.props.moduleState.isUpdating) {
                    if (shippingAddress && this.isBillingAddressSameAsShipping) {
                        this.isBillingAddressSameAsShipping = true;
                        this._updateCurrentOperation(AddressOperation.Show, shippingAddress);
                    } else if (shippingAddress && this.isBillingAddressSameAsShipping) {
                        this._updateCurrentOperation(AddressOperation.Show, shippingAddress);
                    }
                }
            }
        );

        const optionSelected = this._getExtensionPropertyValue('DELIVERYTYPE') || '';
        if (optionSelected.toLocaleLowerCase() === 'opt2') {
            this.isClickAndCollectOrder = true;
            if (this.addressCommon.isAuthenticatedFlow()) {
                this.isBillingAddressSameAsShipping = false;
                this._handleClickAndCollectAuthScenario();
            } else {
                this.isBillingAddressSameAsShipping = false;
                this._handleClickAndCollectUnAuthScenario();
                this.showAddressSearch = true;
            }
        } else {
            this.isClickAndCollectOrder = false;
            this._handleDeliveryScenario();
        }
        reaction(
            () => this.props.data.shippingType.result,
            result => {
                if (result && result.toLocaleUpperCase() === 'COLLECT') {
                    this.isClickAndCollectOrder = true;
                    if (this.addressCommon.isAuthenticatedFlow()) {
                        this.isBillingAddressSameAsShipping = false;
                        this._handleClickAndCollectAuthScenario();
                    } else {
                        const strBillingAddress: string = this._getExtensionPropertyValue('BILLINGADDRESS') || '';
                        if (!isNullOrWhitespace(strBillingAddress)) {
                            this._updateBillingAddress(strBillingAddress);
                        } else {
                            this.isBillingAddressSameAsShipping = false;
                            this._updateCurrentOperation(AddressOperation.Add);
                            this.showAddressSearch = true;
                        }
                    }
                } else {
                    this.isBillingAddressSameAsShipping = true;
                    this.isClickAndCollectOrder = false;
                }
            }
        );
        reaction(
            () => this.isBillingAddressRequried,
            isBillingAddressRequried => {
                if (isBillingAddressRequried) {
                    if (this.props.moduleState.isDisabled) {
                        // this.onSubmit();
                        this.onEdit()
                        this.onSubmit();
                    }
                } else {
                    // tslint:disable-next-line:no-floating-promises
                    this.props.data.checkout.result?.removeBillingAddress({});
                    this.props.moduleState.onDisable();
                }
            }
        );
        if (!this.addressCommon.isAuthenticatedFlow()) {
            this.showBorderClass = '';
        }

        if (autoSuggestionEnabled) {

            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(telemetry, options, channel?.BingMapsApiKey, channel?.ChannelCountryRegionISOCode, channel?.DefaultLanguageId);

            // Customer doesn't have any address. Then add view will be loaded directly. Code for the same to handle that
            if(this.props.data.storeSelectorStateManager.result && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)) {
                await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
            }

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.loadMapApi && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
                async () => {
                    await this._attachMapAutoSuggest();
                }
            );
        }
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }
    public render(): JSX.Element | null {
        if (!(this.isBillingAddressRequried)) {
            return null;
        }

        const { resources } = this.props;

        const { renderView, config } = this.props;

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            className: config.className,
            viewState: {
                isShowAddress: this.selectedAddress && this.currentOperation === AddressOperation.Show,
                isShowAddOrUpdateAddress: this.currentOperation !== AddressOperation.Show,
                isShowSameAsShippingCheckbox: this.isCartContainsItemsForShipping && this.props.moduleState.isUpdating && !this.isClickAndCollectOrder
            },
            CheckoutBillingAddress: {
                moduleProps: this.props,
                className: classnames(`ms-checkout-billing-address ${this.showBorderClass}`, config.className)
            },
            heading: <AddressBillingHeadingComponent {...{ text: resources.addressBillingAddressHeading }} />,
            // tslint:disable-next-line: max-line-length
            sameAsShippingCheckbox: (
                <AddressBillingCheckoxComponent
                    {
                    ...{
                        isChecked: this.isBillingAddressSameAsShipping,
                        type: 'checkbox',
                        value: 'true',
                        ariaLabel: resources.addressSameAsShippingAddressAriaLabel,
                        text: resources.addressSameAsShippingAddressText,
                        telemetryContent: this.telemetryContent,
                        onChange:
                            this._onBillingAddressSameAsShippingChange
                    }
                    }
                />
            ),
            showAddress: this._renderShowAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private _renderSearchAddress = (): React.ReactElement | null => {
        if (this.showAddressSearch && this.isNZ) {
            return (
                <AddressSearch
                    context={this.props.context}
                    manualAddress={this.manualAddress}
                    addressSelectedCallback={this._handleSearchAddressSelection}
                    toggleManualAddressCallback={this._toggleManualAddressInput}
                />
            );
        }
        return null;
    }
    private _handleCartHasOnlyGiftCardScenario = (): void => {
        const { checkout, channelConfiguration } = this.props.data;
        const cart = checkout.result?.checkoutCart.cart;
        const channelGiftCardItemId = channelConfiguration && channelConfiguration.result && channelConfiguration.result.GiftCardItemId || '';
        const cartLinesCount = cart && cart.CartLines && cart.CartLines.length;
        const hasGiftCartLine = cart && cart.CartLines && cart.CartLines.filter(item => item.ItemId && item.ItemId.toLocaleLowerCase() === channelGiftCardItemId.toLocaleLowerCase()).length === 1;
        if (cartLinesCount === 1 && hasGiftCartLine) {
            const defaultAddress = this._getDefaultAddress();
            if (defaultAddress) {
                this._setBillingAddress(defaultAddress);
                this.showBorderClass = '';
                this._updateCurrentOperation(AddressOperation.Show, this.billingAddress);
            }
        }
    }

    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        let addressFormatItems = this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId);
        this.isNZ = this.addUpdateAddress.ThreeLetterISORegionName === this.nzlCountryRegionId;
        this.addressDropdownsData = this.addressFormat.getPrefilledAddressDropdownData(
            this.resources.addressStateDefaultSelectionText,
            this.stateProvinceInfo
        );
        if (this.isNZ && !this.manualAddress) {
            addressFormatItems = addressFormatItems.filter(item => item.name === 'Name' || item.name === 'Phone');
        }
        return AddressAddUpdate(
            {
                resources: this.resources,
                addressType: AddressType.Billing,
                addressFormat: addressFormatItems,
                defaultCountryRegionId: this.countryRegionId,
                defaultAddressType: this.defaultAddressType,
                selectedAddress: this.addUpdateAddress,
                validationError: this.validationError,
                addressActionResponse: this.addressActionResponse,
                dropdownDisplayData: this.addressDropdownsData,
                telemetryContent: this.telemetryContent,
                onInputChange: this._onAddressAddUpdateInputChange,
                onDropdownChange: this._onAddressAddUpdateDropdownChange,
                hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
                onSave: this._onSubmitAddress,
                onCancel: this._resetView,
                searchAddress: this._renderSearchAddress
            }
        );
    }

    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({ address: this.selectedAddress, addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || ''), addressPurposes: this.addressPurposes });
        }

        return null;
    }

    private _handleClickAndCollectAuthScenario = (): void => {
        const defaultAddress = this._getDefaultAddress();
        if (defaultAddress) {
            this._setBillingAddress(defaultAddress);
            this._updateCurrentOperation(AddressOperation.Show, this.billingAddress);
            this.props.moduleState.onReady();
        } else {
            this._updateCurrentOperation(AddressOperation.Add);
        }
    }

    private _handleClickAndCollectUnAuthScenario = (): void => {
        const strBillingAddress: string = this._getExtensionPropertyValue('BILLINGADDRESS') || '';
        if (!isNullOrWhitespace(strBillingAddress)) {
            this._updateBillingAddress(strBillingAddress);
        } else {
            this.isBillingAddressSameAsShipping = false;
            this.isClickAndCollectOrder = true;
            this._updateCurrentOperation(AddressOperation.Add);
        }
    }

    private _handleDeliveryScenario = (): void => {
        const strBillingAddress: string = this._getExtensionPropertyValue('BILLINGADDRESS') || '';
        if (!isNullOrWhitespace(strBillingAddress)) {
           this._updateBillingAddress(strBillingAddress);
        } else {
            this.isBillingAddressSameAsShipping = true;
            this.isClickAndCollectOrder = false;
        }
    }

    private _updateBillingAddress = (strBillingAddress: string) => {
        const billingAddress: Address = JSON.parse(strBillingAddress);
        if (billingAddress) {
            this._setBillingAddress(billingAddress);
            this._updateCurrentOperation(AddressOperation.Show, this.billingAddress);
            this.props.moduleState.onReady();
        }
    }

    private _getDefaultAddress = (): Address | undefined => {
        const strBillingAddress: string = this._getExtensionPropertyValue('BILLINGADDRESS') || '';
        if (!isNullOrWhitespace(strBillingAddress)) {
           return JSON.parse(strBillingAddress);
        }

        if (this.customerAddresses) {
            const primaryAddress = this.customerAddresses.find((address: Address) => address.IsPrimary);
            return primaryAddress || (this.customerAddresses.length > 0 ? this.customerAddresses[0] : undefined);
        }
        return undefined;
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName =  this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
        set(this.addUpdateAddress, { ['TwoLetterISORegionName']: twoLetterISORegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    }

    private _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    }

    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#billing_addressstreet', '#billing_addressstreet_container', this._onSuggestionSelected);
        }
    }

    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { ['Street']: '' });
        set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
        set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
        set(this.addUpdateAddress, { ['City']: address.City });
        set(this.addUpdateAddress, { ['State']: address.State });
        set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
        set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        },         0);

    }

    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId);
        addressFormatItem.forEach(format => {
            if (this.addUpdateAddress[format.name] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
                this.addressFormat[format.name] = '';
            }
        });
        this._clearValidation();
    }

    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    }

    private onAddressAddUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name);
    }

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
        this.onAddressAddUpdate(event.target.name, value);
    }

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    }

    private _getStateProvinces = (): void => {
        if (!this.countryRegionId || !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then(
            (result: StateProvinceInfo[]) => {
                const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);
                // Reset state if selected state not found in the list.
                if (!stateInfo) {
                    set(this.addUpdateAddress, { ['State']: '' });
                }
                this.stateProvinceInfo = result;
            });
    }

    private _initModuleState = (): void => {
        this.props.moduleState.init({
            status: this.isBillingAddressRequried ? 'updating' : 'disabled',
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        const { billingAddress, isBillingAddressSameAsShippingAddress } = this.props.data.checkout.result || {};

        if (this.isBillingAddressRequried) {
            if (billingAddress) {
                this.isBillingAddressSameAsShipping = isBillingAddressSameAsShippingAddress || false;
                this._updateCurrentOperation(AddressOperation.Show, billingAddress);
                this._updateModuleState();
            } else if (this.isCartContainsItemsForShipping) {
                this.isBillingAddressSameAsShipping = true;
                this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
            } else {
                this.isBillingAddressSameAsShipping = false;
                this._updateCurrentOperation(AddressOperation.Add);
                this.showAddressSearch = true;
            }
        }
    }

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _onBillingAddressSameAsShippingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.isBillingAddressSameAsShipping = event.currentTarget.checked;

        if (this.isBillingAddressSameAsShipping && this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
            this.showAddressSearch = false;
        } else {
            this._updateCurrentOperation(AddressOperation.Add, this.billingAddress);
            this.showAddressSearch = true;
        }
    }

    @computed get isCartContainsItemsForShipping(): boolean {
        const { request } = this.props.context;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { channelDeliveryOptionConfig, featureState } = this.props.data;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.
    find(feature => feature.Name === multiplePickupStoreSwitchName)?.IsEnabled;

        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;

        // if hasInvoiceLine is true, there are only invoices no items in cart and return false here
        return this.props.data.checkout.result?.checkoutCart.hasInvoiceLine
            ? false
            : retailMultiplePickUpOptionEnabled ?
            ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).some(
                cartLine => (cartLine.DeliveryMode && cartLine.DeliveryMode !== '')
                    ? (cartLine.DeliveryMode !== channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLine.DeliveryMode) && cartLine.DeliveryMode !== emailDeliveryModeCode)
                    : cartLine
            )
            :((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).some(
                cartLine => (cartLine.DeliveryMode && cartLine.DeliveryMode !== '')
                    ? (cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode)
                    : cartLine
            );
    }

    @computed get billingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.billingAddress;
    }

    @computed get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress;
    }

    @computed get getLoyaltyAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        return checkoutState && checkoutState.loyaltyAmount ? checkoutState.loyaltyAmount : 0;
    }

    @computed get getGiftCardTotalAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.giftCardExtends) {
            return 0;
        }
        return checkoutState.giftCardExtends.reduce(
            (count: number, giftCard: IGiftCardExtend) => {
                return count + (giftCard.Balance || 0);
            },
            0);
    }

    @computed get shouldPaidByCard(): boolean {
        const {
            data: {
                checkout
            },
            config
        } = this.props;
        if (!checkout.result) {
            return false;
        }

        const checkoutResult = checkout.result;
        const cart = checkoutResult.checkoutCart.cart;
        if (!cart || !cart.CartLines || !cart.CartLines.length) {
            return false;
        }

        const { paymentTenderType, tokenizedPaymentCard } = checkoutResult;
        const isPaidByOtherPaymentSource = config.paymenTenderType !== paymentTenderType && tokenizedPaymentCard;

        // Use payment card when it is not free or gift card balance + Loyalty amount can not cover the total amount
        const amountDue = (cart.TotalAmount || 0) - this.getGiftCardTotalAmount - this.getLoyaltyAmount;

        return amountDue > 0 && !isPaidByOtherPaymentSource;
    }

    @computed get isBillingAddressRequried(): boolean {
        return this.shouldPaidByCard;
    }

    private _onSubmitAddress = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            this.props.moduleState.setHasError(true);
            this.manualAddress = true;
            return;
        }

        this._setBillingAddress(this.addUpdateAddress);
        this._updateCurrentOperation(AddressOperation.Show, this.addUpdateAddress);
        this._updateModuleState();
        const strBillingAddress: string = JSON.stringify(this.selectedAddress);
        this._updateExtensionProps('BILLINGADDRESS',strBillingAddress).catch(error => this.props.telemetry.error(`error in _updateExtensionProps ${error}`));

    };

    private _updateExtensionProps= async(key: string, value: string) => {
        await this.props.data.checkout.result?.checkoutCart.updateExtensionProperties(
            {newExtensionProperties: [
                {Key:key, Value: {StringValue: value }}
            ]}
        );
    };

    private onSubmit = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._onSubmitAddress();
                break;
            case AddressOperation.Show:
                if (this.selectedAddress) {
                    const strBillingAddress: string = JSON.stringify(this.selectedAddress);
                    this._setBillingAddress(this.selectedAddress);
                    this._updateExtensionProps('BILLINGADDRESS',strBillingAddress).catch(error => this.props.telemetry.error(`error in _updateExtensionProps ${error}`));
                    this._updateCurrentOperation(AddressOperation.Show, this.selectedAddress);
                    this._updateModuleState();
                }
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onCancel = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._resetView();
                this._clearAddressFields();
                break;
            case AddressOperation.Show:
                // @ts-ignore: Compiler not reconizing property form extending class
                this._resetView();
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onEdit = (): void => {
        if (this.isBillingAddressSameAsShipping && this.shippingAddress && this.isCartContainsItemsForShipping) {
            this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        } else {
            this._updateCurrentOperation(AddressOperation.Add, this.billingAddress);
            this.showAddressSearch = true;
        }
        this.props.moduleState.onUpdating();
    }

    private _setBillingAddress = (address: Address): void => {
        if (this.props.data.checkout.result) {
            const newBillingAddress = { ...address };

            if (address.ThreeLetterISORegionName && !newBillingAddress.TwoLetterISORegionName) {
                newBillingAddress.TwoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(address.ThreeLetterISORegionName);
            }

            // tslint:disable-next-line:no-floating-promises
            this.props.data.checkout.result.updateBillingAddress({ newBillingAddress, additionalProperties: { isBillingAddressSameAsShippingAddress: this.isBillingAddressSameAsShipping } });
        }
    }

    private _resetView = () => {
        this._updateCurrentOperation(AddressOperation.Show, this.billingAddress);
        this._updateModuleState();
        this.showAddressSearch = false;
    }

    private _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        if (selectedAddress === undefined) {
            const name: string = this._getContactNameFromCartExtensions() || '';
            const phone: string = this._getExtensionPropertyValue('PHONENUMBER') || '';
            selectedAddress = {Name: name, Phone: phone};
        }
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            this.addUpdateAddress = { ...this.selectedAddress } || {};
            set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId });
            set(this.addUpdateAddress, { ['AddressTypeValue']: this.addUpdateAddress.AddressTypeValue || this.defaultAddressType });
        }
    }

    private _getContactNameFromCartExtensions = (): string => {
        const firstName = this._getExtensionPropertyValue('FIRSTNAME') || '';
        const lastName = this._getExtensionPropertyValue('LASTNAME') || '';
        return `${firstName} ${lastName}`;

    }

    private _updateModuleState = () => {
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
            this.showAddressSearch = false;
        } else {
            this.props.moduleState.onUpdating();
            this._enableAdressSearch();
        }
    }

    private _enableAdressSearch = (): void => {
        this.showAddressSearch = true;
        this.addUpdateAddress = {
            Name: this.addUpdateAddress.Name,
            Phone: this.addUpdateAddress.Phone,
            ThreeLetterISORegionName: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
        };
    }

    private _toggleManualAddressInput = (): void => {
        this.manualAddress = !this.manualAddress;
    }
    /**
     * callback function for receiving address details from address search component
     */
    private _handleSearchAddressSelection = (address: IFormattedAddressDetails): void => {
        // check fields
        if (!(address && address.country && address.city && address.postcode && address.street && address.suburb)) {
            this.addUpdateAddress = {
                Name: this.addUpdateAddress.Name,
                Phone: this.addUpdateAddress.Phone,
                ThreeLetterISORegionName: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            };
            return;
        }
        // cache name and phone in case of country change
        const name = this.addUpdateAddress.Name;
        const phone = this.addUpdateAddress.Phone;
        // country update - needs 3 letter iso
        const countryCode = this._getCountryThreeLetterCode(address.country);
        if (!countryCode) {
            this.props.telemetry.error('country code undefined for ', address.country);
            return;
        }
        this._elementUpdate('ThreeLetterISORegionName', countryCode);
        // save other address fields
        this._elementUpdate('City', address.city);
        this._elementUpdate('ZipCode', address.postcode);
        this._elementUpdate('Street', address.street);
        this._elementUpdate('StreetNumber', address.street_number ? address.street_number : '');
        this._elementUpdate('BuildingCompliment', address.building_name ? address.building_name : '');
        this._elementUpdate('DistrictName', address.suburb);
        // restore name & phone
        if (name) {
            this._elementUpdate('Name', name);
        }
        if (phone) {
            this._elementUpdate('Phone', phone);
        }
    }

    private _elementUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
    }

    private _getCountryThreeLetterCode = (country: string): string | undefined => {
        const dropdownDisplayData = this.addressDropdownsData.ThreeLetterISORegionName;
        const data = dropdownDisplayData.find((item) => item.value === country);
        return data ? data.key?.toString() : undefined;
    }

    private _getExtensionPropertyValue = (fieldName: string) => {
        const value = this.props.data.checkout.result?.checkoutCart?.cart.ExtensionProperties &&
            this.props.data.checkout.result?.checkoutCart?.cart.ExtensionProperties?.filter(p=>p.Key === fieldName);
        if (value && value?.length > 0) {
            return value[0].Value?.StringValue || undefined;
        }

        return undefined;
    }
}

export default CheckoutBillingAddress;